import axios from "axios";
import { getCached, setCache, invalidateCache } from "@/lib/api-cache";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

let isRedirecting = false;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
});

// Request interceptor - add auth token + cache
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("kc_token");
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }

  // Check cache for GET requests (skip blob downloads)
  if (config.method === "get" && config.responseType !== "blob") {
    const params = config.params ? JSON.stringify(config.params) : "";
    const cacheKey = `${config.url}?${params}`;
    const cached = getCached(cacheKey);
    if (cached) {
      config._fromCache = true;
      config._cachedData = cached;
      config._cacheKey = cacheKey;
      // Use adapter to short-circuit the request
      config.adapter = () => Promise.resolve({ data: cached, status: 200, statusText: "OK", headers: {}, config });
    } else {
      config._cacheKey = cacheKey;
    }
  }

  // Invalidate cache on mutations
  if (config.method !== "get") {
    const url = config.url || "";
    if (url.includes("/clients")) invalidateCache("/clients");
    else if (url.includes("/tasks")) invalidateCache("/tasks");
    else if (url.includes("/invoices")) invalidateCache("/invoices");
    else if (url.includes("/contracts")) invalidateCache("/contracts");
    else if (url.includes("/annexes")) invalidateCache("/annexes");
    else if (url.includes("/calendar")) invalidateCache("/calendar");
    else if (url.includes("/notifications")) invalidateCache("/notifications");
  }

  return config;
});

// Response interceptor - handle 401 + store in cache
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    const key = response.config._cacheKey;
    if (key && response.config.method === "get" && !response.config._fromCache) {
      setCache(key, response.data, response.config.url);
    }
    return response;
  },
  (error) => {
    const status = error?.response?.status;

    // 401 - clear auth and redirect to login
    if (status === 401 && !isRedirecting && window.location.pathname !== "/login") {
      isRedirecting = true;
      clearAuth();
      window.location.href = "/login";
      return new Promise(() => {});
    }

    // Auto-retry on 503/502 (server temporarily unavailable)
    const config = error.config;
    if ((status === 502 || status === 503) && config && !config._retryCount) {
      config._retryCount = 1;
      return new Promise((resolve) => setTimeout(() => resolve(api(config)), 1500));
    }

    return Promise.reject(error);
  }
);

export function setAuthToken(token) {
  if (token) localStorage.setItem("kc_token", token);
  else localStorage.removeItem("kc_token");
}

export function getAuthToken() {
  return localStorage.getItem("kc_token");
}

export function clearAuth() {
  localStorage.removeItem("kc_token");
  localStorage.removeItem("kc_user");
  localStorage.removeItem("kc_last_activity");
}
